import { faBadgeCheck, faComment } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { FormErrors, SubmissionError } from "redux-form";

import { ARTICLE_ADD_EXPERIENCE_FORM_NAME } from "@/mk2/apps/wiki/containers/ArticleAddExperience/ArticleAddExperience.constants";
import {
    NewExperienceFormData,
    Props,
} from "@/mk2/apps/wiki/containers/ArticleAddExperience/ArticleAddExperiencePage";
import Thumbnail from "@/mk2/apps/wiki/containers/ArticleAddExperience/Thumbnail";
import { WIKI_REVIEWS_ARTICLE_SLUG } from "@/mk2/apps/wiki/helpers";
import { getWikiArticleUrl, wikiAddExperienceToUndefinedArticleUrl } from "@/mk2/apps/wiki/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Form } from "@/mk2/components/Form";
import { Img } from "@/mk2/components/Img";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import { CheckboxField } from "@/mk2/components/forms/CheckboxField";
import { InputField } from "@/mk2/components/forms/InputField";
import { StarsRatingField } from "@/mk2/components/forms/StarsRatingField";
import { SubmitButton } from "@/mk2/components/forms/SubmitButton";
import { TextareaField } from "@/mk2/components/forms/TextareaField";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { ComposeToolbar, FIXED } from "@/mk2/containers/Toolbar/Toolbar";
import { MKReduxFormInjectedProps, mkReduxForm } from "@/mk2/decorators/mkReduxForm";
import { url } from "@/mk2/helpers/urls";
import {
    ARTICLE_EXPERIENCE_SEND,
    ARTICLE_THANKS_FOR_EXPERIENCE,
    FORMS_DEFAULT_REQUIRED_FIELD_ERROR,
    PRODUCT_ONE,
    REVIEW_ADD_OWN_PHOTO,
    REVIEW_OWN_EXPERIENCE,
    REVIEW_OWN_PHOTOS,
    REVIEW_VERIFIED,
    REVIEW_WANT_MARK,
    REVIEW_product_I_USED_experience_photos,
    STARS_RATING,
    WIKI_ARTICLE_NEGATIVE_OPTIONAL,
    WIKI_ARTICLE_POSITIVE_OPTIONAL,
    WIKI_DESCRIBE_YOUR_EXPERIENCE,
    WIKI_DO_YOU_HAVE_EXPERIENCE_WITH_ANY_OTHER_REVIEWABLE,
    WIKI_EXP_CREATE_AS_AUTOMSG,
    WIKI_EXP_DRAFT_PREFILLED_EMAIL,
    WIKI_EXP_DRAFT_PREFILLED_PHONE,
    WIKI_EXP_DRAFT_PREFILLED_USERNAME,
    WIKI_EXP_DRAFT_SAVE_AS_DRAFT,
    WIKI_EXP_DRAFT_VERIFIED_BY_PHONE_CALL,
    WIKI_EXP_GUEST_NICKNAME_LABEL,
    WIKI_EXP_TITLE_PLACEHOLDER,
    WIKI_EXP_USER_SPECIFIED_TARGET_PLACEHOLDER,
    WIKI_REVIEWS_NEXT_GOTO_BACK_TO_THE_REVIEW_ARTICLE,
    WIKI_SHARE_YOUR_EXPERIENCE_AND_HELP_OTHERS,
    WIKI_WRITE_ANOTHER_EXPERIENCE,
} from "@/mk/autogenerated/translations/ArticleAddExperience.7305ee036877b90079090ed56186abdd";
import { isAuthenticated } from "@/mk/bazaar/common/userUtils";
import assetUrl from "@/mk/common/assetUrl";
import { ACTIVE_SERVER_ID } from "@/mk/settings";

import styles from "./ArticleAddExperience.mscss";

const THANKEE_STICKER_ID = 34;

const validate = (values: NewExperienceFormData, props: Props) => {
    const errors: FormErrors<NewExperienceFormData> = {};

    if (!values.photos || (values.photos.length === 0 && values.verifiedByExpPhotos === true)) {
        errors.photos = REVIEW_ADD_OWN_PHOTO;
    }

    if (!values.text) {
        errors.text = FORMS_DEFAULT_REQUIRED_FIELD_ERROR;
    }

    if (!values.title) {
        errors.title = FORMS_DEFAULT_REQUIRED_FIELD_ERROR;
    }

    if (props.article && props.article.experiencesStarsAllowed && !values.rating) {
        errors.rating = FORMS_DEFAULT_REQUIRED_FIELD_ERROR;
    }

    // Guest (unregistered) author must provide a display nickname
    if (props.isUnauthenticatedUser && !(values.authorNickname || "").trim()) {
        errors.authorNickname = FORMS_DEFAULT_REQUIRED_FIELD_ERROR;
    }

    if (values.prefilledPhone && !values.prefilledPhone.startsWith("+")) {
        errors.prefilledPhone = "Phone number must start with + (international format)";
    }

    return errors;
};

const emojisConfig = {
    fields: ["text"],
    defaultField: "text",
};

const photosConfig = {
    multiple: true,
    photoType: "wiki",
    showPhotoCode: false,
};

class ArticleAddExperienceComponent extends React.Component<
    Props & MKReduxFormInjectedProps<NewExperienceFormData, Props>
> {
    public render() {
        const { article, articleSlug, categorySlug, loadingState, next, successFlag } = this.props;

        return !successFlag ? (
            <div className={styles.ArticleAddExperience}>
                {articleSlug && categorySlug ? (
                    <LoadingSwitch
                        loadingState={loadingState}
                        onLoad={this.load}
                        onRenderSuccess={this.renderSuccess}
                        hasData={!!article}
                    />
                ) : (
                    this.renderSuccess()
                )}
            </div>
        ) : (
            <div className={styles.ArticleAddExperience}>
                <div className={styles.ArticleAddExperience__addAnother}>
                    <Img
                        width={422}
                        height={300}
                        src={`${assetUrl}img/stickers/${ACTIVE_SERVER_ID}/${THANKEE_STICKER_ID}.png`}
                        className={styles.ArticleAddExperience__addAnother__image}
                    />
                    <h1 className={styles.ArticleAddExperience__addAnother__header}>
                        {ARTICLE_THANKS_FOR_EXPERIENCE}
                    </h1>
                    <p className={styles.ArticleAddExperience__addAnother__message}>
                        {WIKI_DO_YOU_HAVE_EXPERIENCE_WITH_ANY_OTHER_REVIEWABLE}{" "}
                        {WIKI_SHARE_YOUR_EXPERIENCE_AND_HELP_OTHERS}
                    </p>
                    <Btn
                        className={styles.ArticleAddExperience__addAnother__button}
                        type={BtnType.Blue}
                        icon={faComment}
                        label={WIKI_WRITE_ANOTHER_EXPERIENCE}
                        link={url(wikiAddExperienceToUndefinedArticleUrl, {})}
                    />
                    {next && (
                        <Link to={getWikiArticleUrl(WIKI_REVIEWS_ARTICLE_SLUG, next)}>
                            {WIKI_REVIEWS_NEXT_GOTO_BACK_TO_THE_REVIEW_ARTICLE}
                        </Link>
                    )}
                </div>
            </div>
        );
    }

    private load = () => {
        const { articleSlug, categorySlug, onLoad } = this.props;
        if (articleSlug && categorySlug) {
            onLoad(articleSlug, categorySlug);
        }
    };

    private renderSuccess = () => {
        // Note: this render can have article==null (bypassing LoadingSwitch); the experience will be added without defined Article

        const {
            article,
            articleSlug,
            categorySlug,
            handleSubmit,
            isMobile,
            isUnauthenticatedUser,
            loadingState,
            onLoad,
        } = this.props;
        const addingExpToUndefinedArticle = !articleSlug && !categorySlug;
        const hasPositivesNegatives =
            (article && article.experiencesPositivesNegativesAllowed) ||
            addingExpToUndefinedArticle;
        const hasStars =
            (article && article.experiencesStarsAllowed) || addingExpToUndefinedArticle;

        return (
            <div className={styles.ArticleAddExperience}>
                {!addingExpToUndefinedArticle && article && (
                    <Thumbnail
                        articleSlug={articleSlug}
                        categorySlug={categorySlug}
                        article={article}
                        loadingState={loadingState}
                        onLoad={onLoad}
                    />
                )}
                <Form onSubmit={handleSubmit}>
                    {addingExpToUndefinedArticle && (
                        <InputField
                            name="articleProposal"
                            type="text"
                            label={WIKI_EXP_USER_SPECIFIED_TARGET_PLACEHOLDER}
                        />
                    )}

                    {isUnauthenticatedUser && (
                        <InputField
                            name="authorNickname"
                            type="text"
                            label={WIKI_EXP_GUEST_NICKNAME_LABEL}
                        />
                    )}

                    <InputField name="title" type="text" label={WIKI_EXP_TITLE_PLACEHOLDER} />

                    <TextareaField
                        name="text"
                        label={WIKI_DESCRIBE_YOUR_EXPERIENCE}
                        rows={isMobile ? 5 : 7}
                        fillRemainingSpace
                        bottomBorder={Boolean(
                            hasStars || hasPositivesNegatives || (categorySlug && articleSlug),
                        )}
                    />

                    {hasStars && (
                        <StarsRatingField
                            name="rating"
                            starsCount={5}
                            label={STARS_RATING}
                            bottomBorder={Boolean(
                                hasPositivesNegatives || (categorySlug && articleSlug),
                            )}
                        />
                    )}
                    {hasPositivesNegatives && (
                        <TextareaField
                            name="positives"
                            label={"👍 " + WIKI_ARTICLE_POSITIVE_OPTIONAL}
                            rows={3}
                            bottomBorder
                        />
                    )}

                    {hasPositivesNegatives && (
                        <TextareaField
                            name="negatives"
                            label={"👎 " + WIKI_ARTICLE_NEGATIVE_OPTIONAL}
                            rows={3}
                            bottomBorder={Boolean(categorySlug && articleSlug)}
                        />
                    )}

                    {!isUnauthenticatedUser && Boolean(categorySlug && articleSlug) && (
                        <CheckboxField
                            name="createAutomaticPost"
                            label={WIKI_EXP_CREATE_AS_AUTOMSG}
                            bottomBorder={false}
                        />
                    )}
                    {!isUnauthenticatedUser && (
                        <div className={styles.ArticleAddExperience__verifiedReview}>
                            <CheckboxField
                                bottomBorder={false}
                                name="verifiedByExpPhotos"
                                marginLeft
                                label={
                                    <>
                                        <span>
                                            {REVIEW_WANT_MARK}
                                            <FontAwesomeIcon icon={faBadgeCheck} />{" "}
                                            {REVIEW_VERIFIED}:
                                            <div
                                                className={
                                                    styles.ArticleAddExperience__verifiedReview__closed
                                                }
                                            >
                                                <Interpolate
                                                    i18nKey={
                                                        REVIEW_product_I_USED_experience_photos
                                                    }
                                                    product={PRODUCT_ONE}
                                                    experience={<b>{REVIEW_OWN_EXPERIENCE}</b>}
                                                    photos={<b>{REVIEW_OWN_PHOTOS}</b>}
                                                />
                                            </div>
                                        </span>
                                    </>
                                }
                            />
                        </div>
                    )}

                    {!isUnauthenticatedUser && (
                        <ComposeToolbar
                            formName={ARTICLE_ADD_EXPERIENCE_FORM_NAME}
                            emojis={emojisConfig}
                            photos={photosConfig}
                            stickers={false}
                            isFixed={FIXED.IF_MOBILE_BUT_IOS}
                            rightAction={
                                isMobile ? (
                                    <SubmitButton
                                        btnType={BtnType.Blue}
                                        label={ARTICLE_EXPERIENCE_SEND}
                                        formName={ARTICLE_ADD_EXPERIENCE_FORM_NAME}
                                    />
                                ) : null
                            }
                        />
                    )}
                    {isUnauthenticatedUser && (
                        <ComposeToolbar
                            formName={ARTICLE_ADD_EXPERIENCE_FORM_NAME}
                            emojis={emojisConfig}
                            stickers={false}
                            isFixed={FIXED.IF_MOBILE_BUT_IOS}
                            rightAction={
                                isMobile ? (
                                    <SubmitButton
                                        btnType={BtnType.Blue}
                                        label={ARTICLE_EXPERIENCE_SEND}
                                        formName={ARTICLE_ADD_EXPERIENCE_FORM_NAME}
                                    />
                                ) : null
                            }
                        />
                    )}
                    {this.props.isAdmin && this.renderAdminSection()}
                </Form>
            </div>
        );
    };

    private renderAdminSection() {
        const { articleSlug, categorySlug, currentValues, onSaveDraft } = this.props;
        return (
            <div className={styles.ArticleAddExperience__adminSection}>
                <InputField
                    name="prefilledUsername"
                    type="text"
                    label={WIKI_EXP_DRAFT_PREFILLED_USERNAME}
                    isAdminUI
                />
                <InputField
                    name="prefilledPhone"
                    type="text"
                    label={WIKI_EXP_DRAFT_PREFILLED_PHONE}
                    placeholder="+421905123456"
                    isAdminUI
                />
                <InputField
                    name="prefilledEmail"
                    type="text"
                    label={WIKI_EXP_DRAFT_PREFILLED_EMAIL}
                    isAdminUI
                />
                <InputField
                    name="authorNickname"
                    type="text"
                    label={WIKI_EXP_GUEST_NICKNAME_LABEL}
                    isAdminUI
                />
                <CheckboxField
                    name="verifiedByAdmin"
                    label={WIKI_EXP_DRAFT_VERIFIED_BY_PHONE_CALL}
                    isAdminUI
                />
                <div className={styles.ArticleAddExperience__adminButtons}>
                    <Btn
                        type={BtnType.Admin}
                        label={WIKI_EXP_DRAFT_SAVE_AS_DRAFT}
                        onClick={() => onSaveDraft(currentValues, articleSlug, categorySlug)}
                    />
                    {"\u00A0\u00A0"}
                    <Btn
                        type={BtnType.AdminOutline}
                        label="All drafts"
                        link="/adminclub/wiki_experiences/?experiences_tab=drafts"
                        asAnchor
                        linkTarget="_blank"
                        linkRel="noopener"
                    />
                </div>
            </div>
        );
    }
}

const ArticleAddExperience = mkReduxForm<NewExperienceFormData, Props>({
    formName: ARTICLE_ADD_EXPERIENCE_FORM_NAME,
    onValidate: validate,
    onSave: (values, props) => {
        if (isAuthenticated(props.requestUser)) {
            if (
                props.isAdmin &&
                !window.confirm(
                    `Do you want to save experience as "${props.requestUser.username}"?`,
                )
            ) {
                throw new SubmissionError({});
            }
            props.onSave(props.articleSlug, props.categorySlug, values, props.afterSaveGotoUrl);
        } else {
            props.onSaveUnauthenticated(props.articleSlug, props.categorySlug, values);
        }
    },
    photosConfig,
})(ArticleAddExperienceComponent);

export default ArticleAddExperience;
